import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Tooltip,
  Chip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import ReplyIcon from "@mui/icons-material/Reply";
import EditIcon from "@mui/icons-material/Edit";
import type { ProjectMessage, PendingAttachment, User } from "./types";
import { AttachmentPreviewList } from "./AttachmentPreview";

// Allowed file types
const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
];
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

interface MessageInputProps {
  onSend: (content: string) => Promise<void>;
  onEdit?: (messageId: string, content: string) => Promise<void>;
  replyingTo: ProjectMessage | null;
  editingMessage: ProjectMessage | null;
  onCancelReply: () => void;
  onCancelEdit: () => void;
  pendingAttachments: PendingAttachment[];
  onAddAttachment: (file: File) => void;
  onRemoveAttachment: (index: number) => void;
  users: Map<string, User>;
  disabled?: boolean;
}

const getUserName = (userId: string, users: Map<string, User>): string => {
  const user = users.get(userId);
  if (user) {
    return `${user.first_name} ${user.last_name}`.trim() || user.email || "User";
  }
  return "Unknown User";
};

export const MessageInput: React.FC<MessageInputProps> = ({
  onSend,
  onEdit,
  replyingTo,
  editingMessage,
  onCancelReply,
  onCancelEdit,
  pendingAttachments,
  onAddAttachment,
  onRemoveAttachment,
  users,
  disabled = false,
}) => {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Set initial value when editing
  useEffect(() => {
    if (editingMessage) {
      setMessage(editingMessage.content);
      inputRef.current?.focus();
    }
  }, [editingMessage]);

  // Focus input when replying
  useEffect(() => {
    if (replyingTo) {
      inputRef.current?.focus();
    }
  }, [replyingTo]);

  const handleSend = async () => {
    if ((!message.trim() && pendingAttachments.length === 0) || sending) return;

    setSending(true);
    try {
      if (editingMessage && onEdit) {
        await onEdit(editingMessage._id, message);
      } else {
        await onSend(message);
      }
      setMessage("");
    } catch (err) {
      console.error("Failed to send message:", err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === "Escape") {
      if (editingMessage) {
        onCancelEdit();
        setMessage("");
      } else if (replyingTo) {
        onCancelReply();
      }
    }
  };

  const validateAndAddFile = useCallback(
    (file: File) => {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        alert(
          `File type not allowed. Supported types: JPG, PNG, GIF, WebP, PDF`
        );
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        alert(`File too large. Maximum size: 25MB`);
        return;
      }
      onAddAttachment(file);
    },
    [onAddAttachment]
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach(validateAndAddFile);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Drag-drop is handled by ChatPanel - these just prevent default behavior
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  return (
    <Box
      onDragOver={handleDragOver}
      sx={{
        position: "relative",
        borderTop: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      {/* Reply/Edit preview */}
      {(replyingTo || editingMessage) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            bgcolor: editingMessage ? "warning.light" : "action.hover",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {editingMessage ? (
            <EditIcon fontSize="small" color="warning" />
          ) : (
            <ReplyIcon fontSize="small" color="primary" />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {editingMessage
                ? "Editing message"
                : `Replying to ${getUserName(replyingTo!.userId, users)}`}
            </Typography>
            {replyingTo && (
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {replyingTo.content}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={editingMessage ? onCancelEdit : onCancelReply}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Pending attachments */}
      <AttachmentPreviewList
        attachments={pendingAttachments}
        onRemove={onRemoveAttachment}
      />

      {/* Input area */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 1,
          p: 1,
        }}
      >
        {/* File upload button */}
        <Tooltip title="Attach file (drag & drop also works)">
          <IconButton
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || sending}
            color="primary"
          >
            <AttachFileIcon />
          </IconButton>
        </Tooltip>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALLOWED_FILE_TYPES.join(",")}
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />

        {/* Message input */}
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          placeholder={
            editingMessage
              ? "Edit your message..."
              : replyingTo
              ? "Write a reply..."
              : "Type a message..."
          }
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || sending}
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
            },
          }}
        />

        {/* Send button */}
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={
            disabled ||
            sending ||
            (!message.trim() && pendingAttachments.length === 0)
          }
        >
          <SendIcon />
        </IconButton>
      </Box>

    </Box>
  );
};

export default MessageInput;
